import {Button} from 'antd';
import {ButtonType} from 'antd/es/button';
import {FormInstance} from 'antd/es/form';
import cn from 'classnames';
import React, {FC, useState} from 'react';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {DynamicIcon} from 'components/dynamic-icon';
import {TIconType} from 'components/dynamic-icon/dynamic-icon';
import {IUrlConfig} from 'entities/metadata';
import {sendRequestCFTAction} from 'features/provide-microservice-cft/api/api';
import {showMessageFromResponse} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {selectMicroserviceToken, selectUserInfo} from 'store/slices/auth-slice/auth-slice-selectors';

interface IButtonForCFTProps {
    actionTitle: string;
    actionIcon?: TIconType;
    buttonType: ButtonType;
    url?: string;
    form: FormInstance;
    urlConfig?: IUrlConfig;
    onClose?: () => void;
}

interface IConfirmMessageData {
    message: string;
    isConfirmDisabled: boolean;
}

export const ButtonForCFT: FC<IButtonForCFTProps> = ({
    actionTitle,
    actionIcon,
    buttonType,
    url,
    form,
    urlConfig,
    onClose,
    ...props
}: IButtonForCFTProps) => {
    const {userId} = useAppSelector(selectUserInfo) ?? {};
    const token = useAppSelector(selectMicroserviceToken);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState<boolean>(false);
    const [confirmMessageData, setConfirmMessageData] = useState<IConfirmMessageData>({
        isConfirmDisabled: true,
        message: '',
    });
    const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);

    const closeFormAction = () => {
        form.resetFields();
        onClose?.();
    };

    const sendRequestToCFT = async (confirmationRequest: string) => {
        form.validateFields().then(async () => {
            const formValues = form.getFieldsValue();
            try {
                setIsLoadingRequest(true);
                const response = await sendRequestCFTAction({
                    referenceUrl: url,
                    data: {
                        dateFrom: formValues?.dateFrom,
                        dateTo: formValues?.dateTo,
                        documentTypeId: formValues?.typeId?.id,
                        userId,
                        confirmationRequest,
                    },
                    token,
                    urlConfig,
                });
                closeFormAction();
                showMessageFromResponse({response, isError: false});
            } catch (e) {
                const errorMessage = e?.response?.data
                    || 'Произошла непредвиденная ошибка. Обратитесь к администратору';
                const isDisabled = e.response?.status !== 422;

                setConfirmMessageData({isConfirmDisabled: isDisabled, message: errorMessage});
                setIsConfirmModalVisible(true);
            } finally {
                setIsLoadingRequest(false);
            }
        });
    };

    const renderConfirmModal = ({isConfirmDisabled = false, message = ''}) => (
        <ConfirmModal
            title={isConfirmDisabled ? 'Предупреждение' : 'Подтверждение'}
            visible={isConfirmModalVisible}
            content={isConfirmDisabled ? message : [message, 'Вы уверены?']}
            onCancel={() => setIsConfirmModalVisible(false)}
            cancelText={isConfirmDisabled ? 'Закрыть' : 'Нет'}
            onConfirm={() => sendRequestToCFT('Y')}
            approveText={isConfirmDisabled ? '' : 'Да'}
            isConfirmDisabled={isConfirmDisabled}
        />
    );

    const handleClick = () => {
        form?.validateFields();
        sendRequestToCFT('N');
    };

    return (
        <>
            <Button
                {...props}
                onClick={handleClick}
                type={buttonType}
                className={cn('save-action-btn', isLoadingRequest ? 'loading-btn' : '')}
                disabled={isLoadingRequest}
            >
                {actionIcon && <DynamicIcon type={actionIcon} />}
                {actionTitle}
            </Button>
            {isConfirmModalVisible && (
                renderConfirmModal(confirmMessageData)
            )}
        </>
    );
};
